import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings > Securitee > GravityZone (`SettingsSecuriteeGravityZoneController`).
 * Cheia nu vine niciodata inapoi: numai `key_set`.
 */

/** Push pe conexiune: 5 pasi numarati (`GravityZonePush`); `step` = ultimul pas reusit. */
export type GzPush = {
  status: 'off' | 'enabling' | 'active' | 'failed'
  step: number
  steps: number
  failed_step: number | null
  error: string | null
  verified_at: string | null
  last_event_at: string | null
  /** numai la push activ: silent = tacut 2 h si proba de verificare n-a ajuns */
  health?: 'off' | 'ok' | 'quiet' | 'checking' | 'silent'
  silent_minutes?: number | null
  /** dupa un 429 de la GZ: secundele pana cand Retry poate chema iar GZ */
  retry_in?: number | null
}

export type GzConnection = {
  id: number
  scope: 'shared' | 'own'
  name: string
  access_url: string
  key_set: boolean
  checked_at: string | null
  check_ok: boolean | null
  check_error: string | null
  companies_seen: number | null
  updated_at: string | null
  can_edit: boolean
  push?: GzPush | null
}

export type GzLink = {
  connection_id: number
  connection_name: string | null
  gz_company_id: string
  gz_company_name: string
  linked_at: string | null
  synced_at: string | null
  sync_error: string | null
  endpoints: number | null
  matched: number | null
  /** Retelele bifate la Link; null = toate. */
  networks?: string[] | null
}

export type GzPage = {
  canWrite: boolean
  canManageShared: boolean
  cryptoReady: boolean
  connections: GzConnection[]
  link: GzLink | null
  /** Dupa Link si Sync now: daca sincronizarea a pornit in ph4. */
  sync?: { started: boolean; error?: string }
}

export type GzCompany = { id: string; name: string }

/** O retea a firmei din GZ: cate calculatoare are si cate dintre ele sunt si in OD. */
export type GzNetwork = { id: string; name: string; computers: number; in_od: number }

export type GzConnectionForm = { id?: number; scope: 'shared' | 'own'; name: string; access_url: string; api_key: string }

export const GZ_KEY = ['settings', 'securitee', 'gravityzone'] as const

export function gzPageQuery() {
  return queryOptions({
    queryKey: GZ_KEY,
    queryFn: () => apiGet<GzPage>('/settings/securitee/gravityzone'),
  })
}

export function gzSaveConnection(form: GzConnectionForm) {
  const { id, ...rest } = form
  return apiPost<GzPage>('/settings/securitee/gravityzone/connections', id ? { id, ...rest } : rest)
}

export function gzTestConnection(id: number) {
  return apiPost<{ ok: boolean; companies_seen: number | null; error?: string }>(`/settings/securitee/gravityzone/connections/${id}/test`, {})
}

/** `force`: sterge si cand GZ nu a putut opri push-ul. */
export function gzDeleteConnection(id: number, force = false) {
  return apiPost<GzPage>(`/settings/securitee/gravityzone/connections/${id}/delete`, force ? { force: true } : {})
}

export type GzPushAnswer = { connection_id: number; push: GzPush }

/** Pasii 1-3; `replace` inlocuieste push-ul altui sistem din acelasi cont GZ. */
export function gzEnablePush(id: number, replace = false) {
  return apiPost<GzPushAnswer>(`/settings/securitee/gravityzone/connections/${id}/push`, replace ? { replace: true } : {})
}

/** Starea; tot aici se fac pasii 4 si 5 (proba ajunsa in ph4). */
export function gzPushStatus(id: number) {
  return apiGet<GzPushAnswer>(`/settings/securitee/gravityzone/connections/${id}/push`)
}

export function gzPushOff(id: number) {
  return apiPost<GzPushAnswer>(`/settings/securitee/gravityzone/connections/${id}/push/off`, {})
}

export function gzCompanies(id: number) {
  return apiGet<{ companies: GzCompany[]; error?: string }>(`/settings/securitee/gravityzone/connections/${id}/companies`)
}

export function gzNetworks(id: number, gzCompanyId: string) {
  return apiGet<{ networks: GzNetwork[]; error?: string }>(`/settings/securitee/gravityzone/connections/${id}/networks?gz_company_id=${encodeURIComponent(gzCompanyId)}`)
}

/** `networks` lipsa = toate retelele firmei. */
export function gzLink(connectionId: number, gzCompanyId: string, networks?: string[]) {
  return apiPost<GzPage>('/settings/securitee/gravityzone/link', { connection_id: connectionId, gz_company_id: gzCompanyId, ...(networks ? { networks } : {}) })
}

export function gzUnlink() {
  return apiPost<GzPage>('/settings/securitee/gravityzone/unlink', {})
}

/** "Sync now": sincronizarea firmei din antet, pornita acum (fara cron). */
export function gzSyncNow() {
  return apiPost<GzPage>('/settings/securitee/gravityzone/sync', {})
}
